import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Building, 
  Users, 
  Award, 
  Clock, 
  Phone,
  Mail,
  MapPin,
  Home,
  Wrench,
  HardHat,
  CheckCircle,
  Star,
  Shield
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";

// Import images
import londonConstruction from "@/assets/london-construction.jpg";
import residentialConstruction from "@/assets/residential-construction.jpg";
import generalContractingProjects from "@/assets/general-contracting-projects.jpg";

const London = () => {
  const cityData = {
    name: "London",
    region: "Southwestern Ontario",
    population: "420K",
    slug: "london-general-contracting",
    intro: "London is a growing hub for healthcare, education, and technology. Blue Knight Contracting delivers construction solutions for hospitals, universities, residential, and commercial projects.",
    featured_services: [
      "Healthcare Facility Construction",
      "Educational Buildings",
      "Residential Projects",
      "Commercial Spaces"
    ],
    nearby_areas: ["St. Thomas", "Woodstock", "Strathroy", "Sarnia"],
    cta: "Contact us for London construction services."
  };

  const faqData = [
    {
      question: "Do you build healthcare facilities in London?",
      answer: "Yes, we specialize in healthcare facility construction including hospitals, medical clinics, dental offices, and specialized medical facilities throughout London and Southwestern Ontario."
    },
    {
      question: "Can you handle educational building construction?",
      answer: "Absolutely. We have extensive experience with educational construction including schools, university buildings, research facilities, and campus infrastructure projects in London."
    },
    {
      question: "What types of residential projects do you build in London?",
      answer: "We handle all types of residential construction in London including custom homes, residential subdivisions, multi-family housing, student housing, and major home renovations."
    },
    {
      question: "What areas around London do you serve?",
      answer: "We serve London and Southwestern Ontario including St. Thomas, Woodstock, Strathroy, Sarnia, and surrounding communities in the region."
    },
    {
      question: "Do you work with London's universities and colleges?",
      answer: "Yes, we have experience working with Western University, Fanshawe College, and other educational institutions on campus construction and renovation projects."
    },
    {
      question: "Can you build commercial spaces in London's downtown?",
      answer: "Yes, we provide comprehensive commercial construction services including office buildings, retail spaces, restaurants, and mixed-use developments in London's downtown core and business districts."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Blue Knight Contracting London",
    "description": "Professional general contracting and construction services in London and Southwestern Ontario. Specializing in healthcare facilities, educational buildings, and residential projects.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "London",
      "addressRegion": "Ontario",
      "addressCountry": "Canada"
    },
    "telephone": "+1-647-760-8854",
    "url": "https://blueknightcontracting.ca/london",
    "areaServed": {
      "@type": "City",
      "name": "London"
    },
    "serviceType": [
      "General Contracting London",
      "Healthcare Facility Construction",
      "Educational Buildings London",
      "Residential Projects London",
      "Commercial Spaces"
    ]
  };

  const constructionServices = [
    {
      title: "Healthcare Facility Construction",
      description: "Specialized construction services for hospitals, clinics, and medical facilities.",
      icon: Building,
      features: [
        "Hospital construction",
        "Medical clinic build-outs",
        "Dental office construction",
        "Specialized medical facilities",
        "Healthcare renovations",
        "Laboratory construction"
      ]
    },
    {
      title: "Educational Buildings",
      description: "Schools, universities, and educational facility construction throughout London.",
      icon: HardHat,
      features: [
        "School construction",
        "University buildings",
        "Research facilities",
        "Campus infrastructure",
        "Student housing",
        "Educational renovations"
      ]
    },
    {
      title: "Residential Projects",
      description: "Custom homes and residential developments for London's growing population.",
      icon: Home,
      features: [
        "Custom home construction",
        "Residential subdivisions",
        "Multi-family housing",
        "Student housing projects",
        "Home renovations",
        "Affordable housing"
      ]
    },
    {
      title: "Commercial Spaces",
      description: "Modern commercial construction for London's diverse business community.",
      icon: Wrench,
      features: [
        "Office buildings",
        "Retail construction",
        "Restaurant build-outs",
        "Technology centers",
        "Mixed-use developments",
        "Industrial facilities"
      ]
    }
  ];

  return (
    <>
      <SchemaMarkup type="LocalBusiness" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  London General Contracting
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  General Contracting & Construction in London
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  {cityData.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get London Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Call: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Population: {cityData.population}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <span>{cityData.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Licensed & Insured</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={londonConstruction} 
                  alt="Downtown London Ontario skyline with cranes" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">London Construction Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Professional Construction Services in London
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From healthcare facilities to educational buildings, we deliver specialized construction services for London's diverse sectors.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {constructionServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Services List */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Specialties</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Featured London Construction Services
              </h2>
              <p className="text-xl text-muted-foreground">
                Specialized construction services for London's healthcare, education, and technology sectors.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cityData.featured_services.map((service, index) => (
                <Card key={index} className="group hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                      <CheckCircle className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{service}</h3>
                    <p className="text-muted-foreground text-sm">
                      Professional {service.toLowerCase()} services in London and Southwestern Ontario.
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby Service Areas */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Southwestern Ontario Service Areas</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Serving London and Surrounding Areas
              </h2>
              <p className="text-xl text-muted-foreground">
                Professional construction services throughout London and Southwestern Ontario.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <Card className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-primary/5">
                  <div className="flex items-center justify-center gap-2">
                    <Star className="h-4 w-4 text-primary" />
                    <span className="font-semibold">London</span>
                  </div>
                </Card>
                {cityData.nearby_areas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                London Construction FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about construction services in London and Southwestern Ontario.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Start Your London Construction Project?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                {cityData.cta} Get professional construction services from London's trusted general contractors.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">London & SW Ontario</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get London Construction Quote</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our London Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default London;